import LinearProgress from '@mui/material/LinearProgress';
import { DirectUpload } from '@rails/activestorage';
import * as React from 'react';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { File } from 'src/components/Uploader/FileDropzone';
import { useSession } from 'src/contexts/SessionContext';

import {
  InlineFileUploader,
  SafeDefaultAcceptTypes,
} from './InlineFileUploader';

interface UploadingFileWrapper {
  file: File;
  progress: number;
}

export interface UploadDialogProps {
  onClose: () => void;
  onUpload: (files: string[]) => void;
  open: boolean;
  title?: string;
  maxFileSizeCaption?: string;
  accept?: Record<string, string[]>;
}

export function UploadDialog({
  onClose,
  onUpload,
  open = false,
  title = 'Upload files',
  maxFileSizeCaption = 'Max file size is 32 MB',
  accept = SafeDefaultAcceptTypes,
}: UploadDialogProps): React.ReactNode {
  const [files, setFiles] = React.useState<UploadingFileWrapper[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);

  const { user } = useSession();

  React.useEffect(() => {
    setFiles([]);
  }, [open]);

  const handleFileSelect = React.useCallback(
    (newFiles: File | File[] | null) => {
      if (!newFiles) {
        setFiles([]);
        return;
      }
      const filesToAdd = Array.isArray(newFiles) ? newFiles : [newFiles];

      setFiles((prevFiles) => {
        // Keep only files that are still in the new selection
        const remainingFiles = prevFiles.filter((prevFile) =>
          filesToAdd.some(
            (newFile) =>
              prevFile.file.name === newFile.name &&
              prevFile.file.size === newFile.size
          )
        );

        // Add new files that weren't previously selected
        const newUniqueFiles = filesToAdd.filter(
          (newFile) =>
            !remainingFiles.some(
              (existingFile) =>
                existingFile.file.name === newFile.name &&
                existingFile.file.size === newFile.size
            )
        );

        return [
          ...remainingFiles,
          ...newUniqueFiles.map((file) => ({ file, progress: 0 })),
        ];
      });
    },
    []
  );

  const handleRemoveAll = React.useCallback(() => {
    setFiles([]);
  }, []);

  const uploadFile = (fileWrapper: UploadingFileWrapper): Promise<string> => {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(
        fileWrapper.file,
        '/api/v4/direct_uploads',
        {
          directUploadWillCreateBlobWithXHR: (xhr) => {
            xhr.setRequestHeader('API-TOKEN', user.authToken);
          },
          directUploadWillStoreFileWithXHR: (request) => {
            request.upload.addEventListener('progress', (event) => {
              const progress = (event.loaded / event.total) * 100;
              setFiles((prevFiles) =>
                prevFiles.map((f) =>
                  f === fileWrapper
                    ? { ...f, progress: Math.round(progress) }
                    : f
                )
              );
            });
          },
        }
      );

      upload.create((error, blob) => {
        if (error) {
          reject(error);
        } else {
          resolve(blob.signed_id);
        }
      });
    });
  };

  const handleUpload = async () => {
    setIsUploading(true);
    try {
      const uploadedFiles = await Promise.all(
        files.map(async (fileWrapper) => {
          const signedId = await uploadFile(fileWrapper);
          return signedId;
        })
      );
      onUpload(uploadedFiles);
    } catch (error) {
      // Handle error (e.g., show error message)
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      title={title}
      onClose={onClose}
      open={open}
      actions={
        files.length
          ? [
              {
                label: 'Remove all',
                color: 'secondary',
                onClick: handleRemoveAll,
                disabled: isUploading,
              },
              {
                label: 'Upload',
                variant: 'contained',
                onClick: handleUpload,
                disabled: isUploading,
              },
            ]
          : []
      }
    >
      <Stack spacing={3}>
        <InlineFileUploader
          accept={accept}
          maxFileSizeCaption={maxFileSizeCaption}
          onFileSelect={(files) => handleFileSelect(files as File | File[])}
          files={files.map((f) => f.file)}
          multiple={true}
          maxFileSize={32 * 1024 * 1024}
        />
        {isUploading && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        )}
      </Stack>
    </Dialog>
  );
}
